import React, { useEffect, useState, useCallback } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
    fetchOrganizationBillingOverview,
    type OrgBillingOverview,
    type SuperAdminBillingOverviewFilterParams,
} from '../api/plans';
import {
    listSuperAdminOrganizations,
    type SuperAdminOrganization,
} from '../api/superadmin';
import CustomDropdown from '../components/CustomDropdown';
import { useAuth } from '../contexts/AuthContext';

import './SuperAdminPlans-new.css';

function formatDate(iso: string | null | undefined): string
{
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function formatUsdAmount(value: number | null | undefined): string
{
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value ?? 0);
}

function formatLlmUsdAmount(value: number | null | undefined): string
{
    const amount = Number(value ?? 0);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(amount) > 0 && Math.abs(amount) < 0.01 ? 8 : 2,
    }).format(amount);
}

function formatLabel(value: string | null | undefined): string
{
    if (!value) return '—';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatKeySource(value: string | null | undefined): string
{
    if (value === 'byo_key') return 'BYO key';
    if (value === 'platform') return 'Platform';
    return value || '—';
}

function formatUsageReference(entry: OrgBillingOverview['token_usage'][number]): string
{
    if (entry.job_id != null)
    {
        return `Job #${entry.job_id}`;
    }
    if (entry.chat_session_id)
    {
        return `Session ${entry.chat_session_id.length > 22
            ? `${entry.chat_session_id.slice(0, 12)}...${entry.chat_session_id.slice(-6)}`
            : entry.chat_session_id}`;
    }
    if (entry.usage_group_key)
    {
        return entry.usage_group_key.length > 28
            ? `${entry.usage_group_key.slice(0, 18)}...${entry.usage_group_key.slice(-6)}`
            : entry.usage_group_key;
    }
    return '—';
}

function formatPaymentType(paymentType: string | null | undefined, rechargeType: string | null | undefined): string
{
    if (paymentType === 'llm_usage_recharge')
    {
        const labels: Record<string, string> = {
            initial_recharge: 'Initial Credits',
            auto_recharge: 'Auto-Recharge',
            manual_recharge: 'Manual Recharge',
            overage_debt_settlement_recharge: 'Overage Debt Settlement',
        };
        return labels[rechargeType || ''] || 'LLM Usage Credits';
    }
    return 'Subscription';
}

const SA_PAYMENT_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'succeeded', label: 'Succeeded' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
];

const SA_PAYMENT_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'initial_recharge', label: 'Initial Credits' },
    { value: 'auto_recharge', label: 'Auto-Recharge' },
    { value: 'manual_recharge', label: 'Manual Recharge' },
    { value: 'overage_debt_settlement_recharge', label: 'Overage Debt Settlement' },
];

const SA_KEY_SOURCE_OPTIONS = [
    { value: '', label: 'All Keys' },
    { value: 'platform', label: 'Platform' },
    { value: 'byo_key', label: 'BYO key' },
];

const SA_BILLABLE_OPTIONS = [
    { value: '', label: 'All Billing' },
    { value: 'true', label: 'Billable' },
    { value: 'false', label: 'Non-billable' },
];

const SA_BUCKET_OPTIONS = [
    { value: '', label: 'All Buckets' },
    { value: 'free', label: 'Free' },
    { value: 'paid', label: 'Paid' },
    { value: 'system', label: 'System' },
];

type BillingSection = 'payment' | 'token' | 'credit_ledger' | 'quota_ledger';

function SuperAdminOrgBilling(): React.ReactElement
{
    const { user } = useAuth();
    const activeOrgId = Number(user?.active_organization_id || user?.organization_id || 0) || 0;
    const hasActiveOrganizationScope = activeOrgId > 0;
    const [orgs, setOrgs] = useState<SuperAdminOrganization[]>([]);
    const [orgsLoading, setOrgsLoading] = useState(true);
    const [orgsError, setOrgsError] = useState<string | null>(null);

    const [selectedOrgId, setSelectedOrgId] = useState<number | ''>('');
    const [overview, setOverview] = useState<OrgBillingOverview | null>(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [sectionLoading, setSectionLoading] = useState<BillingSection | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [paymentSearchInput, setPaymentSearchInput] = useState('');
    const [paymentStatusInput, setPaymentStatusInput] = useState('');
    const [paymentTypeInput, setPaymentTypeInput] = useState('');
    const [paymentFromDateInput, setPaymentFromDateInput] = useState('');
    const [paymentToDateInput, setPaymentToDateInput] = useState('');
    const [tokenSearchInput, setTokenSearchInput] = useState('');
    const [tokenKeySourceInput, setTokenKeySourceInput] = useState('');
    const [tokenBillableInput, setTokenBillableInput] = useState('');
    const [tokenFromDateInput, setTokenFromDateInput] = useState('');
    const [tokenToDateInput, setTokenToDateInput] = useState('');
    const [creditLedgerSearchInput, setCreditLedgerSearchInput] = useState('');
    const [creditLedgerTypeInput, setCreditLedgerTypeInput] = useState('');
    const [creditLedgerFromDateInput, setCreditLedgerFromDateInput] = useState('');
    const [creditLedgerToDateInput, setCreditLedgerToDateInput] = useState('');
    const [quotaLedgerSearchInput, setQuotaLedgerSearchInput] = useState('');
    const [quotaLedgerBucketInput, setQuotaLedgerBucketInput] = useState('');
    const [quotaLedgerFromDateInput, setQuotaLedgerFromDateInput] = useState('');
    const [quotaLedgerToDateInput, setQuotaLedgerToDateInput] = useState('');
    const [appliedFilters, setAppliedFilters] = useState<SuperAdminBillingOverviewFilterParams>({});

    useEffect(() => {
        if (hasActiveOrganizationScope) {
            setOrgs([]);
            setOrgsLoading(false);
            setOrgsError(null);
            return;
        }

        let cancelled = false;
        async function load() {
            setOrgsLoading(true);
            setOrgsError(null);
            try {
                const orgsResp = await listSuperAdminOrganizations({ page: 1, page_size: 200 });
                if (cancelled) return;
                setOrgs(orgsResp.items);
            }
            catch (exc: any) {
                if (cancelled) return;
                setOrgsError(exc?.response?.data?.detail || exc?.message || 'Failed to load organizations');
            }
            finally {
                if (!cancelled) setOrgsLoading(false);
            }
        }
        void load();
        return () => { cancelled = true; };
    }, [hasActiveOrganizationScope]);

    const loadOverview = useCallback(async (
        orgId: number,
        filters?: SuperAdminBillingOverviewFilterParams,
        section?: BillingSection,
    ) => {
        if (section) setSectionLoading(section);
        else setPageLoading(true);
        setError(null);
        try {
            const data = await fetchOrganizationBillingOverview(orgId, filters);
            setOverview(data);
        }
        catch (exc: any) {
            setError(exc?.response?.data?.detail || exc?.message || 'Failed to load billing overview');
        }
        finally {
            if (section) setSectionLoading(null);
            else setPageLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!hasActiveOrganizationScope) {
            return;
        }
        setSelectedOrgId(activeOrgId);
        setOverview(null);
        void loadOverview(activeOrgId, { paymentLimit: 0, usageLimit: 100, ...appliedFilters });
    }, [activeOrgId, appliedFilters, hasActiveOrganizationScope, loadOverview]);

    function applyPaymentFilters(): void
    {
        if (!selectedOrgId) return;
        const filters: SuperAdminBillingOverviewFilterParams = { ...appliedFilters, paymentLimit: 0, usageLimit: 100 };
        if (paymentSearchInput.trim()) filters.payment_search = paymentSearchInput.trim();
        else delete filters.payment_search;
        if (paymentStatusInput) filters.payment_status = paymentStatusInput;
        else delete filters.payment_status;
        if (paymentTypeInput) filters.payment_type = paymentTypeInput;
        else delete filters.payment_type;
        if (paymentFromDateInput) filters.payment_date_from = paymentFromDateInput;
        else delete filters.payment_date_from;
        if (paymentToDateInput) filters.payment_date_to = paymentToDateInput;
        else delete filters.payment_date_to;
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'payment');
    }

    function clearPaymentFilters(): void
    {
        if (!selectedOrgId) return;
        setPaymentSearchInput('');
        setPaymentStatusInput('');
        setPaymentTypeInput('');
        setPaymentFromDateInput('');
        setPaymentToDateInput('');
        const {
            payment_search: _paymentSearch,
            payment_status: _paymentStatus,
            payment_type: _paymentType,
            payment_date_from: _paymentDateFrom,
            payment_date_to: _paymentDateTo,
            ...rest
        } = appliedFilters;
        const filters: SuperAdminBillingOverviewFilterParams = { ...rest, paymentLimit: 100, usageLimit: 100 };
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'payment');
    }

    function applyTokenFilters(): void
    {
        if (!selectedOrgId) return;
        const filters: SuperAdminBillingOverviewFilterParams = { ...appliedFilters, paymentLimit: 100, usageLimit: 100 };
        if (tokenSearchInput.trim()) filters.token_usage_search = tokenSearchInput.trim();
        else delete filters.token_usage_search;
        if (tokenKeySourceInput) filters.token_usage_key_source = tokenKeySourceInput;
        else delete filters.token_usage_key_source;
        if (tokenBillableInput === 'true') filters.token_usage_billable = true;
        else if (tokenBillableInput === 'false') filters.token_usage_billable = false;
        else delete filters.token_usage_billable;
        if (tokenFromDateInput) filters.token_usage_date_from = tokenFromDateInput;
        else delete filters.token_usage_date_from;
        if (tokenToDateInput) filters.token_usage_date_to = tokenToDateInput;
        else delete filters.token_usage_date_to;
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'token');
    }

    function clearTokenFilters(): void
    {
        if (!selectedOrgId) return;
        setTokenSearchInput('');
        setTokenKeySourceInput('');
        setTokenBillableInput('');
        setTokenFromDateInput('');
        setTokenToDateInput('');
        const {
            token_usage_search: _tokenSearch,
            token_usage_key_source: _tokenKeySource,
            token_usage_billable: _tokenBillable,
            token_usage_date_from: _tokenDateFrom,
            token_usage_date_to: _tokenDateTo,
            ...rest
        } = appliedFilters;
        const filters: SuperAdminBillingOverviewFilterParams = { ...rest, paymentLimit: 100, usageLimit: 100 };
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'token');
    }

    function applyCreditLedgerFilters(): void
    {
        if (!selectedOrgId) return;
        const filters: SuperAdminBillingOverviewFilterParams = { ...appliedFilters, paymentLimit: 100, usageLimit: 100 };
        if (creditLedgerSearchInput.trim()) filters.credit_ledger_search = creditLedgerSearchInput.trim();
        else delete filters.credit_ledger_search;
        if (creditLedgerTypeInput.trim()) filters.credit_ledger_entry_type = creditLedgerTypeInput.trim();
        else delete filters.credit_ledger_entry_type;
        if (creditLedgerFromDateInput) filters.credit_ledger_date_from = creditLedgerFromDateInput;
        else delete filters.credit_ledger_date_from;
        if (creditLedgerToDateInput) filters.credit_ledger_date_to = creditLedgerToDateInput;
        else delete filters.credit_ledger_date_to;
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'credit_ledger');
    }

    function clearCreditLedgerFilters(): void
    {
        if (!selectedOrgId) return;
        setCreditLedgerSearchInput('');
        setCreditLedgerTypeInput('');
        setCreditLedgerFromDateInput('');
        setCreditLedgerToDateInput('');
        const {
            credit_ledger_search: _creditLedgerSearch,
            credit_ledger_entry_type: _creditLedgerType,
            credit_ledger_date_from: _creditLedgerDateFrom,
            credit_ledger_date_to: _creditLedgerDateTo,
            ...rest
        } = appliedFilters;
        const filters: SuperAdminBillingOverviewFilterParams = { ...rest, paymentLimit: 100, usageLimit: 100 };
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'credit_ledger');
    }

    function applyQuotaLedgerFilters(): void
    {
        if (!selectedOrgId) return;
        const filters: SuperAdminBillingOverviewFilterParams = { ...appliedFilters, paymentLimit: 100, usageLimit: 100 };
        if (quotaLedgerSearchInput.trim()) filters.quota_ledger_search = quotaLedgerSearchInput.trim();
        else delete filters.quota_ledger_search;
        if (quotaLedgerBucketInput) filters.quota_ledger_balance_bucket = quotaLedgerBucketInput;
        else delete filters.quota_ledger_balance_bucket;
        if (quotaLedgerFromDateInput) filters.quota_ledger_date_from = quotaLedgerFromDateInput;
        else delete filters.quota_ledger_date_from;
        if (quotaLedgerToDateInput) filters.quota_ledger_date_to = quotaLedgerToDateInput;
        else delete filters.quota_ledger_date_to;
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'quota_ledger');
    }

    function clearQuotaLedgerFilters(): void
    {
        if (!selectedOrgId) return;
        setQuotaLedgerSearchInput('');
        setQuotaLedgerBucketInput('');
        setQuotaLedgerFromDateInput('');
        setQuotaLedgerToDateInput('');
        const {
            quota_ledger_search: _quotaLedgerSearch,
            quota_ledger_balance_bucket: _quotaLedgerBucket,
            quota_ledger_date_from: _quotaLedgerDateFrom,
            quota_ledger_date_to: _quotaLedgerDateTo,
            ...rest
        } = appliedFilters;
        const filters: SuperAdminBillingOverviewFilterParams = { ...rest, paymentLimit: 100, usageLimit: 100 };
        setAppliedFilters(filters);
        void loadOverview(Number(selectedOrgId), filters, 'quota_ledger');
    }


    return (
        <div className="sa-plans-page">
            <div className="sa-plans-breadcrumbs">Super Admin · Organization Billing</div>
            <div className="sa-plans-header-row">
                <div>
                    <h1 className="sa-plans-title">Organization Billing</h1>
                    <p className="sa-plans-subtitle">
                        Read-only view of subscription status, LLM Usage Credits, and payment history for any organization.
                    </p>
                </div>
            </div>

            {orgsError && <div className="sa-plans-error">{orgsError}</div>}

            {!hasActiveOrganizationScope && (
            <div className="sa-plans-card sa-orgbilling-picker">
                <label htmlFor="sa-org-select" className="sa-orgbilling-picker-label">Organization</label>
                {orgsLoading ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <CustomDropdown
                        value={selectedOrgId === '' ? '' : String(selectedOrgId)}
                        options={[
                            { value: '', label: '— select an organization —' },
                            ...orgs.map((org) => ({
                                value: String(org.id),
                                label: `${org.name} (#${org.id}) · ${org.user_count} users`,
                            })),
                        ]}
                        onChange={(v) => {
                            if (!v) {
                                setSelectedOrgId('');
                                setOverview(null);
                                return;
                            }
                            const id = Number(v);
                            setSelectedOrgId(id);
                            setOverview(null);
                            void loadOverview(id, { paymentLimit: 0, usageLimit: 100, ...appliedFilters });
                        }}
                        placeholder="— select an organization —"
                    />
                )}
            </div>
            )}

            {error && <div className="sa-plans-error">{error}</div>}

            {pageLoading && !overview && (
                <div className="sa-plans-card sa-plans-empty">
                    <Spinner animation="border" size="sm" /> Loading billing overview…
                </div>
            )}

            {overview && (
                <div className="sa-orgbilling-sections">
                    {/* Current Plan */}
                    <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">Current Plan</h2>
                        {overview.current_plan ? (
                            <div className="sa-orgbilling-plan-summary">
                                <div><strong>Name:</strong> {overview.current_plan.name}</div>
                                <div><strong>Type:</strong> {overview.current_plan.plan_type}</div>
                                <div><strong>Interval:</strong> {overview.current_plan.billing_interval}</div>
                                <div>
                                    <strong>Price:</strong>{' '}
                                    {overview.current_plan.plan_type === 'free'
                                        ? 'Free'
                                        : `${(overview.current_plan.price_cents / 100).toFixed(2)} ${overview.current_plan.currency.toUpperCase()}`}
                                </div>
                                <div>
                                    <strong>User Limit:</strong>{' '}
                                    {overview.current_plan.user_limit == null ? 'Unlimited' : overview.current_plan.user_limit}
                                </div>
                                <div>
                                    <strong>Features:</strong>{' '}
                                    {overview.current_plan.feature_flags.length === 0
                                        ? '—'
                                        : overview.current_plan.feature_flags.join(', ')}
                                </div>
                            </div>
                        ) : (
                            <div className="sa-plans-empty">Organization is not currently subscribed to a plan.</div>
                        )}
                    </div>

                    {/* Billing Usage */}
                    <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">Billing Usage</h2>
                        <div className="sa-orgbilling-quota-grid">
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Subscription</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.has_active_subscription ? 'Active' : 'Inactive'}
                                </div>
                                <div className="sa-orgbilling-quota-sub">{overview.quota.subscription_status ?? '—'}</div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Free tokens remaining</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.free_tokens_remaining.toLocaleString()}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    {overview.quota.free_tokens_consumed.toLocaleString()} used of{' '}
                                    {overview.quota.free_tokens_allocated.toLocaleString()}
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Pending Debt</div>
                                <div className="sa-orgbilling-quota-value">
                                    {formatUsdAmount(overview.llm_usage_credits.pending_overage_debt_usd)}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    {overview.llm_usage_credits.pending_overage_debt_usd > 0
                                        ? 'Deducted from the next successful recharge'
                                        : 'No outstanding debt'}
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Paid Platform Tokens</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.all_time_paid_platform_tokens.toLocaleString()}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    All-time platform usage with settled free overage
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Available LLM Usage Credits</div>
                                <div className="sa-orgbilling-quota-value">
                                    {formatUsdAmount(overview.llm_usage_credits.available_balance_after_debt_usd)}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    Available for platform-key usage
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">BYO recorded tokens</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.byo_recorded_tokens.toLocaleString()}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    {formatUsdAmount(overview.quota.byo_recorded_charge_usd)} customer charge
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">May run job</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.may_run_job ? 'Yes' : 'No'}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    {overview.quota.block_reason ?? overview.quota.next_action_required ?? '—'}
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Period</div>
                                <div className="sa-orgbilling-quota-value">
                                    {formatDate(overview.quota.period_end)}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    Started {formatDate(overview.quota.period_start)}
                                </div>
                            </div>
                            <div className="sa-orgbilling-quota-tile">
                                <div className="sa-orgbilling-quota-label">Active source</div>
                                <div className="sa-orgbilling-quota-value">
                                    {overview.quota.active_balance_source ?? '—'}
                                </div>
                                <div className="sa-orgbilling-quota-sub">
                                    Free overage debt: {overview.quota.free_overage_debt.toLocaleString()} / {formatUsdAmount(overview.quota.free_overage_debt_usd || 0)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment History */}
                    {false && <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">Payment History</h2>
                        <div className="sa-orgbilling-filters">
                            <input className="sa-orgbilling-filter-input" type="text" placeholder="Search payments..." value={paymentSearchInput} onChange={(e) => setPaymentSearchInput(e.target.value)} />
                            <CustomDropdown value={paymentStatusInput} options={SA_PAYMENT_STATUS_OPTIONS} onChange={(v) => setPaymentStatusInput(v)} placeholder="All Statuses" />
                            <CustomDropdown value={paymentTypeInput} options={SA_PAYMENT_TYPE_OPTIONS} onChange={(v) => setPaymentTypeInput(v)} placeholder="All Types" />
                            <input className="sa-orgbilling-filter-input" type="date" value={paymentFromDateInput} onChange={(e) => setPaymentFromDateInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="date" value={paymentToDateInput} onChange={(e) => setPaymentToDateInput(e.target.value)} />
                            <button type="button" className="sa-orgbilling-filter-btn" onClick={() => applyPaymentFilters()} disabled={sectionLoading === 'payment'}>Search</button>
                            {!!(appliedFilters.payment_search || appliedFilters.payment_status || appliedFilters.payment_type || appliedFilters.payment_date_from || appliedFilters.payment_date_to) && (
                                <button type="button" className="sa-orgbilling-filter-clear" onClick={() => clearPaymentFilters()}>Clear</button>
                            )}
                        </div>
                        {sectionLoading === 'payment' ? (
                            <div className="sa-plans-empty"><Spinner animation="border" size="sm" /> Searching payments…</div>
                        ) : overview.payments.length === 0 ? (
                            <div className="sa-plans-empty">No payments recorded.</div>
                        ) : (
                            <table className="sa-plans-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Period</th>
                                        <th>Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.payments.map((p) => (
                                        <tr key={p.id}>
                                            <td data-label="Date">{formatDate(p.created_at)}</td>
                                            <td data-label="Type">{formatPaymentType(p.payment_type, p.recharge_type)}</td>
                                            <td data-label="Amount">{p.amount_usd.toFixed(2)} {p.currency.toUpperCase()}</td>
                                            <td data-label="Status">
                                                <span className={`sa-plans-pill ${p.status === 'succeeded' ? 'sa-plans-pill-active' : 'sa-plans-pill-inactive'}`}>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td data-label="Period">{formatDate(p.period_start)} → {formatDate(p.period_end)}</td>
                                            <td data-label="Receipt">
                                                {p.receipt_url ? (
                                                    <a href={p.receipt_url} target="_blank" rel="noreferrer">View</a>
                                                ) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>}

                    {/* Token Usage */}
                    <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">Token Usage</h2>
                        <div className="sa-orgbilling-filters">
                            <input className="sa-orgbilling-filter-input" type="text" placeholder="Search usage..." value={tokenSearchInput} onChange={(e) => setTokenSearchInput(e.target.value)} />
                            <CustomDropdown value={tokenKeySourceInput} options={SA_KEY_SOURCE_OPTIONS} onChange={(v) => setTokenKeySourceInput(v)} placeholder="All Keys" />
                            <CustomDropdown value={tokenBillableInput} options={SA_BILLABLE_OPTIONS} onChange={(v) => setTokenBillableInput(v)} placeholder="All Billing" />
                            <input className="sa-orgbilling-filter-input" type="date" value={tokenFromDateInput} onChange={(e) => setTokenFromDateInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="date" value={tokenToDateInput} onChange={(e) => setTokenToDateInput(e.target.value)} />
                            <button type="button" className="sa-orgbilling-filter-btn" onClick={() => applyTokenFilters()} disabled={sectionLoading === 'token'}>Search</button>
                            {!!(appliedFilters.token_usage_search || appliedFilters.token_usage_key_source || appliedFilters.token_usage_billable !== undefined || appliedFilters.token_usage_date_from || appliedFilters.token_usage_date_to) && (
                                <button type="button" className="sa-orgbilling-filter-clear" onClick={() => clearTokenFilters()}>Clear</button>
                            )}
                        </div>
                        {sectionLoading === 'token' ? (
                            <div className="sa-plans-empty"><Spinner animation="border" size="sm" /> Searching token usage…</div>
                        ) : overview.token_usage.length === 0 ? (
                            <div className="sa-plans-empty">No token usage recorded.</div>
                        ) : (
                            <div className="sa-orgbilling-table-wrap">
                            <table className="sa-plans-table sa-orgbilling-token-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Reference</th>
                                        <th>Feature</th>
                                        <th>Calls</th>
                                        <th>Model</th>
                                        <th>Stage</th>
                                        <th>Key</th>
                                        <th>Billable</th>
                                        <th>Provider Cost</th>
                                        <th>Customer Charge</th>
                                        <th>Prompt</th>
                                        <th>Cached</th>
                                        <th>Completion</th>
                                        <th>Total</th>
                                        <th>Job</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.token_usage.map((row) => (
                                        <tr key={row.id}>
                                            <td data-label="Date">{formatDate(row.created_at)}</td>
                                            <td data-label="Reference">{formatUsageReference(row)}</td>
                                            <td data-label="Feature">{row.usage_label ?? formatLabel(row.workflow_profile)}</td>
                                            <td data-label="Calls">{(row.event_count ?? 1).toLocaleString()}</td>
                                            <td data-label="Model">{row.model ?? '—'}</td>
                                            <td data-label="Stage">{formatLabel(row.stage)}</td>
                                            <td data-label="Key">{formatKeySource(row.key_source)}</td>
                                            <td data-label="Billable">{row.billable ? 'Billable' : 'Non-billable'}</td>
                                            <td data-label="Provider Cost">{formatLlmUsdAmount(row.provider_cost_usd)}</td>
                                            <td data-label="Customer Charge">{formatLlmUsdAmount(row.customer_charge_usd)}</td>
                                            <td data-label="Prompt">{row.prompt_tokens.toLocaleString()}</td>
                                            <td data-label="Cached">{row.cached_input_tokens.toLocaleString()}</td>
                                            <td data-label="Completion">{row.completion_tokens.toLocaleString()}</td>
                                            <td data-label="Total">{row.total_tokens.toLocaleString()}</td>
                                            <td data-label="Job">{row.job_id ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>

                    {/* Ledger */}
                    <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">LLM Usage Credits Ledger</h2>
                        <div className="sa-orgbilling-filters">
                            <input className="sa-orgbilling-filter-input" type="text" placeholder="Search credit ledger..." value={creditLedgerSearchInput} onChange={(e) => setCreditLedgerSearchInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="text" placeholder="Entry type" value={creditLedgerTypeInput} onChange={(e) => setCreditLedgerTypeInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="date" value={creditLedgerFromDateInput} onChange={(e) => setCreditLedgerFromDateInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="date" value={creditLedgerToDateInput} onChange={(e) => setCreditLedgerToDateInput(e.target.value)} />
                            <button type="button" className="sa-orgbilling-filter-btn" onClick={() => applyCreditLedgerFilters()} disabled={sectionLoading === 'credit_ledger'}>Search</button>
                            {!!(appliedFilters.credit_ledger_search || appliedFilters.credit_ledger_entry_type || appliedFilters.credit_ledger_date_from || appliedFilters.credit_ledger_date_to) && (
                                <button type="button" className="sa-orgbilling-filter-clear" onClick={() => clearCreditLedgerFilters()}>Clear</button>
                            )}
                        </div>
                        {sectionLoading === 'credit_ledger' ? (
                            <div className="sa-plans-empty"><Spinner animation="border" size="sm" /> Searching credit ledger…</div>
                        ) : overview.llm_usage_credit_ledger.length === 0 ? (
                            <div className="sa-plans-empty">No credit ledger entries.</div>
                        ) : (
                            <table className="sa-plans-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Balance</th>
                                        <th>Debt</th>
                                        <th>Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.llm_usage_credit_ledger.map((row) => (
                                        <tr key={row.id}>
                                            <td data-label="Date">{formatDate(row.created_at)}</td>
                                            <td data-label="Type">{formatLabel(row.entry_type)}</td>
                                            <td data-label="Amount">{formatUsdAmount(row.amount_usd)}</td>
                                            <td data-label="Balance">{formatUsdAmount(row.balance_after_cents / 100)}</td>
                                            <td data-label="Debt">{formatUsdAmount(row.pending_overage_after_cents / 100)}</td>
                                            <td data-label="Receipt">
                                                {row.stripe_receipt_url ? (
                                                    <a href={row.stripe_receipt_url} target="_blank" rel="noreferrer">View</a>
                                                ) : row.stripe_error ? 'Failed' : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="sa-plans-card">
                        <h2 className="sa-orgbilling-section-title">Quota Ledger</h2>
                        <div className="sa-orgbilling-filters">
                            <input className="sa-orgbilling-filter-input" type="text" placeholder="Search quota ledger..." value={quotaLedgerSearchInput} onChange={(e) => setQuotaLedgerSearchInput(e.target.value)} />
                            <CustomDropdown value={quotaLedgerBucketInput} options={SA_BUCKET_OPTIONS} onChange={(v) => setQuotaLedgerBucketInput(v)} placeholder="All Buckets" />
                            <input className="sa-orgbilling-filter-input" type="date" value={quotaLedgerFromDateInput} onChange={(e) => setQuotaLedgerFromDateInput(e.target.value)} />
                            <input className="sa-orgbilling-filter-input" type="date" value={quotaLedgerToDateInput} onChange={(e) => setQuotaLedgerToDateInput(e.target.value)} />
                            <button type="button" className="sa-orgbilling-filter-btn" onClick={() => applyQuotaLedgerFilters()} disabled={sectionLoading === 'quota_ledger'}>Search</button>
                            {!!(appliedFilters.quota_ledger_search || appliedFilters.quota_ledger_balance_bucket || appliedFilters.quota_ledger_date_from || appliedFilters.quota_ledger_date_to) && (
                                <button type="button" className="sa-orgbilling-filter-clear" onClick={() => clearQuotaLedgerFilters()}>Clear</button>
                            )}
                        </div>
                        {sectionLoading === 'quota_ledger' ? (
                            <div className="sa-plans-empty"><Spinner animation="border" size="sm" /> Searching quota ledger…</div>
                        ) : overview.ledger.length === 0 ? (
                            <div className="sa-plans-empty">No ledger entries.</div>
                        ) : (
                            <table className="sa-plans-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Event</th>
                                        <th>Bucket</th>
                                        <th>Δ Tokens</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.ledger.map((row) => (
                                        <tr key={row.id}>
                                            <td data-label="Date">{formatDate(row.created_at)}</td>
                                            <td data-label="Event">{row.event_type}</td>
                                            <td data-label="Bucket">{row.balance_bucket}</td>
                                            <td data-label="Token Change">{row.token_delta.toLocaleString()}</td>
                                            <td data-label="Notes">{row.notes ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SuperAdminOrgBilling;
